import { SignedMoneyText } from "../../../../../../features/shifts/signed-money.js";
import {
  BUSINESS_UNIT_LABELS,
  BUSINESS_UNIT_TYPES,
  type BusinessUnitType,
  type ReportFile,
  SHIFT_CLOSING_STATUS_LABELS,
  SHIFT_CLOSING_WARNING_LABELS,
  SHIFT_SETTLEMENT_TARGET_LABELS,
  SHIFT_TENDER_TYPE_LABELS,
  type Shift,
  type ShiftClosing,
  STAFF_DUTY_LABELS,
  TRY_DENOMINATIONS,
} from "@station/contracts";
import { Money } from "@station/core";
import {
  Alert,
  Button,
  Card,
  cn,
  Dialog,
  FormField,
  Input,
  MoneyInput,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  Minus,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { Fragment, type ReactElement, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  downloadReportFile,
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../../../../features/counterparties/shared.js";
import { useTaxRates } from "../../../../../../features/expenses/shared.js";
import { ReopenClosingDialog } from "../../../../../../features/shifts/closing-dialogs.js";
import {
  cashCountedOf,
  CLOSING_STEPS,
  type ClosingFormValues,
  type ClosingStep,
  closingStepProblems,
  closingToForm,
  countedTotal,
  declarationPreview,
  denominationRowTotal,
  differenceDirection,
  differenceTone,
  emptyManualSaleRow,
  emptyTenderRow,
  isClosingEditable,
  type ManualSaleRow,
  manualSalesTotal,
  nextClosingStep,
  nextPendingMemberId,
  OTHER_TENDER_TYPES,
  parseSaveClosing,
  previousClosingStep,
  SHIFT_CLOSING_STATUS_TONE,
  type TenderRow,
  tenderRowsTotal,
} from "../../../../../../features/shifts/closing-values.js";
import {
  canEditClosingOf,
  useRefreshShifts,
  useShift,
  useShiftClosing,
  useShiftClosings,
  useShiftPermissions,
} from "../../../../../../features/shifts/queries.js";
import { ShiftsPage } from "../../../../../../features/shifts/shared.js";
import { formatLitres, formatMinor } from "../../../../../../features/shifts/shift-values.js";
import { usePosTerminals } from "../../../../../../features/treasury/definitions.js";
import { api } from "../../../../../../lib/api.js";
import { errorMessage, isApiError } from "../../../../../../lib/errors.js";
import { formatDateTime } from "../../../../../../lib/format.js";
import { useMe } from "../../../../../../lib/session.js";

const searchSchema = z.object({
  step: z.enum(CLOSING_STEPS).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/vardiya/vardiyalar/$id/hesap/$memberId")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ClosingWizardPage,
});

/**
 * POMPACI / KASİYER HESABI SİHİRBAZI — tablet için TAM SAYFA (pencere değil).
 *
 * Adımlar: satış (beklenen) → nakit sayımı → kart (POS) → veresiye ve diğer araçlar → özet ve teslim.
 * Her "İleri"de taslak `PUT` ile kaydedilir ve SUNUCUNUN hesapladığı tutarlar gösterilir; iyimser kilit
 * `version` ile korunur (`VERSION_CONFLICT` → tazeleme önerilir).
 *
 * Adım URL'de (`?step=`) taşınır: kaydetme sonrası form sunucudan gelen sürümle YENİDEN kurulduğunda
 * (aşağıdaki `key`) kullanıcı adımını kaybetmez; sayfa yenilense ya da bağlantı paylaşılsa da aynı adım açılır.
 */
function ClosingWizardPage(): ReactElement {
  const { t } = useTranslation();
  const { id, memberId } = Route.useParams();
  const permissions = useShiftPermissions();
  const shift = useShift(id);
  const closing = useShiftClosing(id, memberId);
  const staffName = closing.data?.staffName ?? "";
  const title = closing.data
    ? `${closing.data.shiftNo} · ${closing.data.staffCode}`
    : t("shifts.closing.wizardTitle");

  return (
    <ShiftsPage
      title={title}
      description={staffName ? t("shifts.closing.wizardDescription", { staff: staffName }) : undefined}
      breadcrumb={[
        {
          label: t("menu.shifts"),
          link: (children, className) => (
            <Link to="/vardiya/vardiyalar" className={className}>
              {children}
            </Link>
          ),
        },
        {
          label: closing.data?.shiftNo ?? t("shifts.detail.title"),
          link: (children, className) => (
            <Link to="/vardiya/vardiyalar/$id" params={{ id }} className={className}>
              {children}
            </Link>
          ),
        },
        { label: t("shifts.closing.wizardTitle") },
      ]}
    >
      {() =>
        !permissions.canRead ? (
          <Alert tone="info">{t("shifts.noPermission")}</Alert>
        ) : closing.error ? (
          <Alert tone="danger">{errorMessage(closing.error)}</Alert>
        ) : shift.error ? (
          <Alert tone="danger">{errorMessage(shift.error)}</Alert>
        ) : !closing.data || !shift.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <ClosingWizard
            // Sunucu yeni bir SÜRÜM verdiğinde (kaydetme, teslim, yeniden açma) form o fotoğrafla kurulur.
            key={`${closing.data.id}:${closing.data.version}:${closing.data.status}`}
            shift={shift.data as Shift}
            closing={closing.data as ShiftClosing}
          />
        )
      }
    </ShiftsPage>
  );
}

function ClosingWizard({ shift, closing }: { shift: Shift; closing: ShiftClosing }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const refresh = useRefreshShifts();
  const permissions = useShiftPermissions();
  const myUserId = useMe().data?.user.id ?? null;
  const siblings = useShiftClosings(shift.id, permissions.canRead);

  const member = shift.members.find((item) => item.id === closing.memberId);
  const step: ClosingStep = search.step ?? "expected";
  const [values, setValues] = useState<ClosingFormValues>(() => closingToForm(closing));
  const [issues, setIssues] = useState<Array<{ path: string; message: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reopening, setReopening] = useState(false);

  const mayEdit = canEditClosingOf(permissions, member, myUserId);
  const editable =
    isClosingEditable(closing.status) &&
    mayEdit &&
    !shift.businessDayLocked &&
    (shift.status === "OPEN" || shift.status === "CLOSING");
  /** Teslim, vardiya KAPANIŞA geçmeden yapılamaz: beklenen tutar kapanış endeksleriyle kesinleşir. */
  const canSubmit = editable && shift.status === "CLOSING";

  const setStep = (next: ClosingStep) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => ({ ...previous, step: next })) as never,
      replace: true,
    });

  /**
   * KASİYER hesabında beklenenin KAYNAĞI (ADR 0015): kasa satışı varsa `POS`, yoksa eski davranış `MANUAL`.
   * `POS` iken satış özeti SUNUCUNUN türettiği satırlardır → ekranda kilitlidir ve beklenen tutar sunucunun
   * hesabından okunur (elle toplam alınmaz).
   */
  const expectedSource = closing.expectedDetail.expectedSource;
  const expectedAmount =
    closing.kind === "CASHIER" && expectedSource === "MANUAL"
      ? manualSalesTotal(values.manualSales).toString()
      : closing.expectedAmount;

  const preview = declarationPreview({
    cashCounted: cashCountedOf(values),
    openingFloat: closing.openingFloat,
    cashDrops: closing.cashDrops,
    cashExpenses: closing.cashExpenses,
    derivedTenders: closing.tenders.filter((tender) => tender.derived),
    clientRows: [...values.cardRows, ...values.otherRows],
    expectedAmount,
  });
  const problems = closingStepProblems(values, step, closing.kind, expectedSource);

  const patch = (changes: Partial<ClosingFormValues>) => setValues((current) => ({ ...current, ...changes }));

  const handleError = (caught: unknown) => {
    if (isApiError(caught, "VERSION_CONFLICT")) {
      setConflict(true);
      setError(errorMessage(caught));
      return;
    }
    setError(errorMessage(caught));
  };

  /** Taslağı kaydeder ve sunucunun hesapladığı hesabı döndürür (her "İleri" bunu çağırır). */
  const save = useMutation({
    mutationFn: async () => {
      const parsed = parseSaveClosing(values, closing.kind, expectedSource);
      if (!parsed.ok) {
        setIssues(parsed.issues);
        throw new Error(t("shifts.records.validationFailed"));
      }
      setIssues([]);
      return (
        await api.PUT("/shifts/{id}/closings/{memberId}", {
          params: { path: { id: shift.id, memberId: closing.memberId } },
          body: parsed.body as never,
        })
      ).data!;
    },
    onError: handleError,
  });

  const recalculate = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/shifts/{id}/closings/{memberId}/recalculate", {
          params: { path: { id: shift.id, memberId: closing.memberId } },
        })
      ).data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("shifts.closing.recalculated") });
    },
    onError: handleError,
  });

  const submit = useMutation({
    mutationFn: async () => {
      // Teslimden ÖNCE beyan kaydedilir: sunucu sürümü artar ve teslim o sürümle yapılır.
      const saved = await save.mutateAsync();
      const explanation = values.explanation.trim();
      return (
        await api.POST("/shifts/{id}/closings/{memberId}/submit", {
          params: { path: { id: shift.id, memberId: closing.memberId } },
          body: { version: (saved as ShiftClosing).version, explanation: explanation || null },
        })
      ).data!;
    },
    onSuccess: async (result) => {
      setConfirming(false);
      await refresh();
      const status = (result as ShiftClosing).status;
      toast({
        tone: status === "ACCEPTED" ? "success" : "info",
        title: t(`shifts.closing.submitted.${status === "ACCEPTED" ? "accepted" : "pending"}`),
      });
    },
    onError: handleError,
  });

  const download = useMutation({
    mutationFn: async () =>
      (
        await api.GET("/shifts/{id}/closings/{memberId}/handover.pdf", {
          params: { path: { id: shift.id, memberId: closing.memberId } },
        })
      ).data!,
    onSuccess: (file) => downloadReportFile(file as ReportFile),
    onError: handleError,
  });

  /** "İleri": önce taslak kaydedilir (sunucunun hesapladığı tutarlar görünsün), sonra adım ilerler. */
  const goNext = async () => {
    const next = nextClosingStep(step);
    if (next === null) return;
    setError(null);
    if (editable) {
      try {
        await save.mutateAsync();
      } catch {
        // Hata `onError` ile zaten gösterildi; adım İLERLEMEZ ki kullanıcı düzeltebilsin.
        return;
      }
      await refresh();
    }
    setStep(next);
  };

  const busy = save.isPending || submit.isPending || recalculate.isPending;
  const nextMemberId = nextPendingMemberId(siblings.data ?? [], closing.memberId);

  return (
    // Yapışkan alt çubuk içeriği ÖRTMESİN: alt boşluk çubuğun yüksekliğinden fazladır (klavye açıkken de).
    <div className="grid gap-4 pb-48">
      <Card className="grid gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-lg font-semibold">
            {closing.staffCode} — {closing.staffName}
          </span>
          <StatusBadge tone="neutral">{STAFF_DUTY_LABELS[closing.kind]}</StatusBadge>
          <StatusBadge tone={SHIFT_CLOSING_STATUS_TONE[closing.status]}>
            {SHIFT_CLOSING_STATUS_LABELS[closing.status]}
          </StatusBadge>
          {closing.provisional ? (
            <StatusBadge tone="warning">{t("shifts.closing.provisionalBadge")}</StatusBadge>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          {closing.shiftNo} · {shift.stationName} · {formatDateTime(shift.openedAt)}
        </p>
      </Card>

      {closing.status === "REJECTED" && closing.rejectedReason ? (
        <Alert tone="danger" title={t("shifts.closing.rejectedTitle")}>
          {closing.rejectedReason}
        </Alert>
      ) : null}
      {closing.provisional ? (
        <Alert tone="warning" title={t("shifts.closing.provisionalTitle")}>
          {t("shifts.closing.provisionalBody")}
        </Alert>
      ) : null}
      {!mayEdit && isClosingEditable(closing.status) ? (
        <Alert tone="info">{t("shifts.closing.noEditPermission")}</Alert>
      ) : null}
      {shift.businessDayLocked ? <Alert tone="warning">{t("shifts.closing.dayLocked")}</Alert> : null}
      {conflict ? (
        <Alert tone="warning" title={t("shifts.closing.conflictTitle")}>
          <div className="grid gap-2">
            <p>{t("shifts.closing.conflictBody")}</p>
            <div>
              <Button variant="outline" size="sm" onClick={() => void refresh()}>
                <RefreshCw aria-hidden="true" />
                {t("shifts.settings.reload")}
              </Button>
            </div>
          </div>
        </Alert>
      ) : null}
      {error && !conflict ? <Alert tone="danger">{error}</Alert> : null}
      {issues.length > 0 ? (
        <Alert tone="danger" title={t("shifts.records.validationFailed")}>
          <ul className="list-inside list-disc">
            {issues.slice(0, 5).map((issue) => (
              <li key={`${issue.path}:${issue.message}`}>{issue.message}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {!isClosingEditable(closing.status) ? (
        <ResultPanel
          closing={closing}
          shiftId={shift.id}
          nextMemberId={nextMemberId}
          canReopen={permissions.canManageClosing && closing.status === "ACCEPTED"}
          downloading={download.isPending}
          onDownload={() => download.mutate()}
          onReopen={() => setReopening(true)}
        />
      ) : null}

      <StepStrip step={step} onSelect={setStep} />

      {step === "expected" ? (
        <ExpectedStep
          closing={closing}
          values={values}
          editable={editable}
          onChange={patch}
          onRecalculate={() => recalculate.mutate()}
          recalculating={recalculate.isPending}
        />
      ) : null}
      {step === "cash" ? (
        <CashStep closing={closing} values={values} editable={editable} onChange={patch} preview={preview} />
      ) : null}
      {step === "card" ? (
        <CardStep shift={shift} values={values} editable={editable} onChange={patch} problems={problems} />
      ) : null}
      {step === "other" ? (
        <OtherStep
          shiftId={shift.id}
          closing={closing}
          values={values}
          editable={editable}
          onChange={patch}
          problems={problems}
        />
      ) : null}
      {step === "summary" ? (
        <SummaryStep
          closing={closing}
          values={values}
          editable={editable}
          canSubmit={canSubmit}
          shiftStatus={shift.status}
          preview={preview}
          onChange={patch}
        />
      ) : null}

      <StickyBar
        expected={expectedAmount}
        declared={preview.declared.toString()}
        difference={preview.difference.toString()}
        withinTolerance={closing.withinTolerance}
        step={step}
        busy={busy}
        editable={editable}
        canSubmit={canSubmit}
        problems={problems.length > 0}
        onBack={() => {
          const previous = previousClosingStep(step);
          if (previous) setStep(previous);
        }}
        onNext={() => void goNext()}
        onSubmit={() => setConfirming(true)}
      />

      <Dialog
        open={confirming}
        onOpenChange={(next) => {
          if (!submit.isPending) setConfirming(next);
        }}
        title={t("shifts.closing.submitTitle")}
        description={t("shifts.closing.submitDescription")}
        dismissible={!submit.isPending}
      >
        <div className="grid gap-4">
          <dl className="grid gap-2 sm:grid-cols-3">
            <SummaryField label={t("shifts.closing.expected")}>
              <MoneyText value={closing.expectedAmount} />
            </SummaryField>
            <SummaryField label={t("shifts.closing.declared")}>
              <MoneyText value={preview.declared.toString()} />
            </SummaryField>
            <SummaryField label={t("shifts.closing.difference")}>
              <SignedMoneyText value={preview.difference.toString()} />
            </SummaryField>
          </dl>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirming(false)}
              disabled={submit.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="button" loading={submit.isPending} onClick={() => submit.mutate()}>
              <Check aria-hidden="true" />
              {t("shifts.closing.submit")}
            </Button>
          </div>
        </div>
      </Dialog>

      <ReopenClosingDialog
        open={reopening}
        onOpenChange={setReopening}
        shiftId={shift.id}
        memberId={closing.memberId}
        staffName={closing.staffName}
      />
    </div>
  );
}

// ── Adım şeridi ──────────────────────────────────────────────────────────────
function StepStrip({
  step,
  onSelect,
}: {
  step: ClosingStep;
  onSelect: (step: ClosingStep) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <ol className="flex flex-wrap gap-2" aria-label={t("shifts.closing.steps.label")}>
      {CLOSING_STEPS.map((item, index) => {
        const active = item === step;
        return (
          <li key={item}>
            <Button
              variant={active ? "primary" : "outline"}
              size="lg"
              aria-current={active ? "step" : undefined}
              onClick={() => onSelect(item)}
            >
              <span aria-hidden="true">{index + 1}.</span>
              {t(`shifts.closing.steps.${item}`)}
            </Button>
          </li>
        );
      })}
    </ol>
  );
}

// ── Yapışkan alt özet çubuğu ─────────────────────────────────────────────────
function StickyBar({
  expected,
  declared,
  difference,
  withinTolerance,
  step,
  busy,
  editable,
  canSubmit,
  problems,
  onBack,
  onNext,
  onSubmit,
}: {
  expected: string;
  declared: string;
  difference: string;
  withinTolerance: boolean;
  step: ClosingStep;
  busy: boolean;
  editable: boolean;
  canSubmit: boolean;
  problems: boolean;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const direction = differenceDirection(difference);
  const isLast = nextClosingStep(step) === null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t bg-card/95 p-3 backdrop-blur">
      <div className="mx-auto grid max-w-5xl gap-3">
        {/*
          Canlı özet: kullanıcı yazarken tutar değişir; ekran okuyucuya kibarca duyurulur. Ad taşıyan kap
          `<section>`tır (adlandırılmış bölge); `aria-label` doğrudan `<dl>` üzerinde DURMAZ.
        */}
        <section aria-live="polite" aria-label={t("shifts.closing.liveSummary")}>
          <dl className="grid grid-cols-3 gap-2 text-center">
            <SummaryField label={t("shifts.closing.expected")}>
              <MoneyText value={expected} className="text-base font-semibold" />
            </SummaryField>
            <SummaryField label={t("shifts.closing.declared")}>
              <MoneyText value={declared} className="text-base font-semibold" />
            </SummaryField>
            <SummaryField label={t("shifts.closing.difference")}>
              <span className="grid justify-items-center gap-1">
                <SignedMoneyText value={difference} className="text-base font-semibold" />
                <StatusBadge tone={differenceTone(difference, withinTolerance)}>
                  {t(`shifts.closing.direction.${direction}`)}
                </StatusBadge>
              </span>
            </SummaryField>
          </dl>
        </section>
        <div className="flex flex-wrap justify-between gap-2">
          <Button variant="outline" size="lg" onClick={onBack} disabled={previousClosingStep(step) === null}>
            <ArrowLeft aria-hidden="true" />
            {t("common.back")}
          </Button>
          {isLast ? (
            <Button size="lg" onClick={onSubmit} disabled={!canSubmit || busy || problems}>
              <Check aria-hidden="true" />
              {t("shifts.closing.submit")}
            </Button>
          ) : (
            <Button size="lg" loading={busy && editable} onClick={onNext} disabled={problems}>
              {t("common.next")}
              <ArrowRight aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryField({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactElement {
  const headingId = useId();
  return (
    <Card className="grid gap-3 p-4" aria-labelledby={headingId}>
      <h2 id={headingId} className="text-base font-semibold">
        {title}
      </h2>
      {children}
    </Card>
  );
}

function ProblemList({ problems }: { problems: readonly string[] }): ReactElement | null {
  const { t } = useTranslation();
  if (problems.length === 0) return null;
  return (
    <Alert tone="warning" title={t("shifts.closing.stepProblemsTitle")}>
      <ul className="list-inside list-disc">
        {problems.map((problem) => (
          <li key={problem}>{t(`shifts.closing.problem.${problem}`)}</li>
        ))}
      </ul>
    </Alert>
  );
}

// ── 1. Satış (beklenen) ──────────────────────────────────────────────────────
function ExpectedStep({
  closing,
  values,
  editable,
  onChange,
  onRecalculate,
  recalculating,
}: {
  closing: ShiftClosing;
  values: ClosingFormValues;
  editable: boolean;
  onChange: (changes: Partial<ClosingFormValues>) => void;
  onRecalculate: () => void;
  recalculating: boolean;
}): ReactElement {
  const { t } = useTranslation();
  if (closing.kind === "CASHIER")
    return (
      <ManualSalesStep
        closing={closing}
        values={values}
        editable={editable && closing.expectedDetail.expectedSource === "MANUAL"}
        onChange={onChange}
      />
    );

  const detail = closing.expectedDetail;
  return (
    <div className="grid gap-4">
      {closing.expectedWarnings.length > 0 ? (
        <Alert tone="warning" title={t("shifts.closing.warningsTitle")}>
          <ul className="list-inside list-disc">
            {closing.expectedWarnings.map((warning) => (
              <li key={warning}>{SHIFT_CLOSING_WARNING_LABELS[warning]}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <Section title={t("shifts.closing.steps.expected")}>
        <p className="text-sm text-muted-foreground">{t("shifts.closing.expectedHint")}</p>
        <SimpleTable
          caption={t("shifts.closing.intervalsCaption")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("shifts.records.nozzle")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("shifts.fields.openingTotalizer")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("shifts.closing.closingTotalizer")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("shifts.closing.totalizerLitres")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("shifts.closing.testLitres")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("shifts.closing.saleLitres")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("shifts.records.amount")}
              </th>
            </tr>
          }
          foot={
            <tr>
              <td className={tdClass} colSpan={4}>
                {t("common.total")}
              </td>
              <td className={tdRightClass}>{formatLitres(detail.testLitres)}</td>
              <td className={tdRightClass}>{formatLitres(detail.saleLitres)}</td>
              <td className={tdRightClass}>
                <MoneyText value={detail.amount} />
              </td>
            </tr>
          }
        >
          {detail.intervals.map((interval) => (
            <Fragment key={interval.assignmentId}>
              <tr className="border-t align-top">
                <td className={tdClass}>
                  {interval.nozzleLabel}
                  <br />
                  <span className="text-xs text-muted-foreground">{interval.productName}</span>
                  {/* Fiyat vardiya içinde değiştiyse dilimler ALT SATIRDA ayrı ayrı yazılır. */}
                  {interval.segments.length > 1 ? (
                    <ul className="mt-1 grid gap-0.5 text-xs text-muted-foreground">
                      {interval.segments.map((segment) => (
                        <li key={`${segment.from}-${segment.unitPrice}`}>
                          {formatDateTime(segment.from)} · {segment.unitPrice} ₺/L ·{" "}
                          {formatLitres(segment.saleLitres)} · {formatMinor(segment.amount)}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </td>
                <td className={tdRightClass}>{formatLitres(interval.openingTotalizer)}</td>
                <td className={tdRightClass}>{formatLitres(interval.closingTotalizer)}</td>
                <td className={tdRightClass}>{formatLitres(interval.totalizerLitres)}</td>
                <td className={tdRightClass}>{formatLitres(interval.testLitres)}</td>
                <td className={tdRightClass}>{formatLitres(interval.saleLitres)}</td>
                <td className={tdRightClass}>
                  <MoneyText value={interval.amount} />
                </td>
              </tr>
              {/* Uyarılar AYRI, tam genişlikte satırda: dar tabanca sütunundaki rozet tablette 9 satıra bölünüyordu. */}
              {interval.warnings.length > 0 ? (
                <tr>
                  <td className={`${tdClass} pt-0`} colSpan={7}>
                    <ul className="grid gap-0.5 text-xs text-warning">
                      {interval.warnings.map((warning) => (
                        <li key={warning} className="flex items-start gap-1.5">
                          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                          <span>{SHIFT_CLOSING_WARNING_LABELS[warning]}</span>
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </SimpleTable>

        {detail.byProduct.length > 0 ? (
          <SimpleTable
            caption={t("shifts.closing.byProductCaption")}
            head={
              <tr>
                <th scope="col" className={thClass}>
                  {t("shifts.records.product")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("shifts.closing.saleLitres")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("shifts.closing.testLitres")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("shifts.records.amount")}
                </th>
              </tr>
            }
          >
            {detail.byProduct.map((product) => (
              <tr key={product.productId} className="border-t">
                <td className={tdClass}>{product.productName}</td>
                <td className={tdRightClass}>{formatLitres(product.saleLitres)}</td>
                <td className={tdRightClass}>{formatLitres(product.testLitres)}</td>
                <td className={tdRightClass}>
                  <MoneyText value={product.amount} />
                </td>
              </tr>
            ))}
          </SimpleTable>
        ) : null}

        {editable ? (
          <div>
            <Button variant="outline" size="lg" loading={recalculating} onClick={onRecalculate}>
              <RefreshCw aria-hidden="true" />
              {t("shifts.closing.recalculate")}
            </Button>
          </div>
        ) : null}
      </Section>
    </div>
  );
}

/**
 * KASİYER hesabında beklenen tutarın kaynağı iki türlüdür (ADR 0015):
 *  · `MANUAL` — kasiyerin ELLE girdiği satış özeti (ADR 0013 karar 14; Prompt 15 öncesi davranış),
 *  · `POS` — istasyonun yerel kasa satışları. Bu durumda satırlar SUNUCUNUN türettiği özet satırlarıdır:
 *    ekran salt okunur olur, hiçbir satır gövdeye geri gönderilmez (sunucu `400 pos_expected` verir) ve
 *    kullanıcı kasa fişlerine bağlantıyla gider.
 */
function ManualSalesStep({
  closing,
  values,
  editable,
  onChange,
}: {
  closing: ShiftClosing;
  values: ClosingFormValues;
  editable: boolean;
  onChange: (changes: Partial<ClosingFormValues>) => void;
}): ReactElement {
  const { t } = useTranslation();
  const taxRates = useTaxRates();
  const vatRates = (taxRates.data ?? []).filter((rate) => rate.taxType === "VAT");
  const fromPos = closing.expectedDetail.expectedSource === "POS";
  const patchRow = (rowId: string, changes: Partial<ManualSaleRow>) =>
    onChange({
      manualSales: values.manualSales.map((row) => (row.rowId === rowId ? { ...row, ...changes } : row)),
    });

  return (
    <Section title={fromPos ? t("shifts.closing.posSalesTitle") : t("shifts.closing.manualSalesTitle")}>
      {fromPos ? (
        <Alert tone="info" title={t("shifts.closing.posExpectedTitle")}>
          <div className="grid gap-2">
            <p>{t("shifts.closing.posExpectedBody", { count: values.manualSales.length })}</p>
            <div>
              <Link
                to="/kasa/satislar"
                search={{ shift: closing.shiftId, staff: closing.staffId } as never}
                className="text-sm underline underline-offset-4"
              >
                {t("shifts.closing.posExpectedLink")}
              </Link>
            </div>
          </div>
        </Alert>
      ) : null}
      <p className="text-sm text-muted-foreground">
        {fromPos ? t("shifts.closing.posSalesHint") : t("shifts.closing.manualSalesHint")}
      </p>
      <ul className="grid gap-3">
        {values.manualSales.map((row, index) => (
          <li key={row.rowId} className="grid gap-3 rounded-md border p-3 sm:grid-cols-2">
            <FormField label={t("shifts.closing.businessUnit")}>
              {(control) => (
                <Select
                  {...control}
                  className="min-h-11"
                  disabled={!editable}
                  value={row.businessUnit}
                  onChange={(event) =>
                    patchRow(row.rowId, { businessUnit: event.target.value as BusinessUnitType })
                  }
                >
                  {BUSINESS_UNIT_TYPES.map((unit) => (
                    <option key={unit} value={unit}>
                      {BUSINESS_UNIT_LABELS[unit]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField
              label={t("shifts.closing.revenueGroup")}
              description={t("shifts.closing.revenueGroupHint")}
            >
              {(control) => (
                <Input
                  {...control}
                  className="min-h-11"
                  disabled={!editable}
                  value={row.revenueGroup}
                  maxLength={40}
                  autoCapitalize="characters"
                  onChange={(event) => patchRow(row.rowId, { revenueGroup: event.target.value })}
                />
              )}
            </FormField>
            <FormField label={t("shifts.records.vatRate")}>
              {(control) => (
                <Select
                  {...control}
                  className="min-h-11"
                  disabled={!editable}
                  value={row.vatRateCode}
                  onChange={(event) => patchRow(row.rowId, { vatRateCode: event.target.value })}
                >
                  <option value="">{t("common.select")}</option>
                  {vatRates.map((rate) => (
                    <option key={rate.code} value={rate.code}>
                      {rate.description ?? rate.code} (%{rate.rate})
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("shifts.closing.gross")} description={t("shifts.records.amountVatHint")}>
              {(control) => (
                <MoneyInput
                  {...control}
                  className="min-h-11"
                  disabled={!editable}
                  value={row.gross}
                  onValueChange={(gross) => patchRow(row.rowId, { gross })}
                />
              )}
            </FormField>
            {editable ? (
              <div className="sm:col-span-2">
                <Button
                  variant="ghost"
                  size="lg"
                  aria-label={t("shifts.closing.removeManualSale", { no: index + 1 })}
                  onClick={() =>
                    onChange({ manualSales: values.manualSales.filter((item) => item.rowId !== row.rowId) })
                  }
                >
                  <Trash2 aria-hidden="true" />
                  {t("common.delete")}
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {editable ? (
        <div>
          <Button
            variant="outline"
            size="lg"
            onClick={() =>
              onChange({
                manualSales: [...values.manualSales, emptyManualSaleRow(`ms-${Date.now()}`)],
              })
            }
          >
            <Plus aria-hidden="true" />
            {t("shifts.closing.addRow")}
          </Button>
        </div>
      ) : null}
      <p className="text-sm">
        {t("shifts.closing.expected")}:{" "}
        <MoneyText
          value={fromPos ? closing.expectedAmount : manualSalesTotal(values.manualSales).toString()}
          className="font-semibold"
        />
      </p>
    </Section>
  );
}

// ── 2. Nakit sayımı ──────────────────────────────────────────────────────────
function CashStep({
  closing,
  values,
  editable,
  onChange,
  preview,
}: {
  closing: ShiftClosing;
  values: ClosingFormValues;
  editable: boolean;
  onChange: (changes: Partial<ClosingFormValues>) => void;
  preview: ReturnType<typeof declarationPreview>;
}): ReactElement {
  const { t } = useTranslation();
  const counted = countedTotal(values.denominations);
  const setQuantity = (value: string, quantity: number | null) =>
    onChange({ denominations: { ...values.denominations, [value]: quantity } });
  const step = (value: string, delta: number) => {
    const current = values.denominations[value] ?? 0;
    setQuantity(value, Math.max(0, Math.min(current + delta, 1_000_000)));
  };

  return (
    <div className="grid gap-4">
      <Section title={t("shifts.closing.steps.cash")}>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-5 rounded border-input accent-[var(--primary)]"
            disabled={!editable}
            checked={values.withDenominations}
            onChange={(event) => onChange({ withDenominations: event.target.checked })}
          />
          {t("shifts.records.withDenominations")}
        </label>

        {values.withDenominations ? (
          <SimpleTable
            caption={t("shifts.records.denominationsCaption")}
            head={
              <tr>
                <th scope="col" className={thRightClass}>
                  {t("shifts.records.denomination")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("shifts.records.quantity")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("common.total")}
                </th>
              </tr>
            }
            foot={
              <tr>
                <td className={tdClass} colSpan={2}>
                  {t("shifts.records.countedTotal")}
                </td>
                <td className={tdRightClass}>
                  <MoneyText value={counted.toString()} className="font-semibold" />
                </td>
              </tr>
            }
          >
            {TRY_DENOMINATIONS.map((value) => {
              const quantity = values.denominations[value] ?? null;
              const label = Money.fromMinor(value).formatTr();
              return (
                <tr key={value} className="border-t">
                  <td className={cn(tdRightClass, "text-base font-medium")}>{label}</td>
                  <td className={tdClass}>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        className="size-11 p-0"
                        disabled={!editable}
                        aria-label={t("shifts.closing.decrease", { denomination: label })}
                        onClick={() => step(value, -1)}
                      >
                        <Minus aria-hidden="true" />
                      </Button>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        step={1}
                        className="h-11 w-24 text-center text-base"
                        disabled={!editable}
                        aria-label={t("shifts.records.quantityFor", { denomination: label })}
                        value={quantity === null ? "" : String(quantity)}
                        onChange={(event) => {
                          const digits = event.target.value.replace(/\D/g, "");
                          setQuantity(value, digits === "" ? null : Math.min(Number(digits), 1_000_000));
                        }}
                      />
                      <Button
                        variant="outline"
                        className="size-11 p-0"
                        disabled={!editable}
                        aria-label={t("shifts.closing.increase", { denomination: label })}
                        onClick={() => step(value, 1)}
                      >
                        <Plus aria-hidden="true" />
                      </Button>
                    </div>
                  </td>
                  <td className={tdRightClass}>
                    {quantity ? (
                      <MoneyText value={denominationRowTotal(value, quantity).toString()} />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </SimpleTable>
        ) : (
          <FormField label={t("shifts.closing.cashCounted")}>
            {(control) => (
              <MoneyInput
                {...control}
                className="min-h-11 text-base"
                disabled={!editable}
                value={values.cashCounted}
                onValueChange={(cashCounted) => onChange({ cashCounted })}
              />
            )}
          </FormField>
        )}
      </Section>

      <Section title={t("shifts.closing.cashFactsTitle")}>
        {/* Bu üç sayı SUNUCUDAN gelir (vardiya içi kayıtlar); burada değiştirilemez. */}
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryField label={t("shifts.fields.openingFloat")}>
            <MoneyText value={closing.openingFloat} />
          </SummaryField>
          <SummaryField label={t("shifts.fields.cashDropTotal")}>
            <MoneyText value={closing.cashDrops} />
          </SummaryField>
          <SummaryField label={t("shifts.fields.cashExpenseTotal")}>
            <MoneyText value={closing.cashExpenses} />
          </SummaryField>
          <SummaryField label={t("shifts.closing.cashCounted")}>
            <MoneyText value={cashCountedOf(values).toString()} />
          </SummaryField>
        </dl>
        <p className="rounded-md bg-muted/60 p-3 text-sm">
          {t("shifts.closing.cashFormula")}{" "}
          <MoneyText value={preview.cashSales.toString()} className="font-semibold" />
        </p>
      </Section>
    </div>
  );
}

// ── 3. Kart (POS) ────────────────────────────────────────────────────────────
function CardStep({
  shift,
  values,
  editable,
  onChange,
  problems,
}: {
  shift: Shift;
  values: ClosingFormValues;
  editable: boolean;
  onChange: (changes: Partial<ClosingFormValues>) => void;
  problems: readonly string[];
}): ReactElement {
  const { t } = useTranslation();
  const terminals = usePosTerminals(shift.companyId);
  const options = (terminals.data ?? []).filter(
    (terminal) => terminal.stationId === shift.stationId && terminal.isActive,
  );
  const patchRow = (rowId: string, changes: Partial<TenderRow>) =>
    onChange({
      cardRows: values.cardRows.map((row) => (row.rowId === rowId ? { ...row, ...changes } : row)),
    });

  return (
    <div className="grid gap-4">
      <ProblemList problems={problems} />
      <Section title={t("shifts.closing.steps.card")}>
        <p className="text-sm text-muted-foreground">{t("shifts.closing.cardHint")}</p>
        {options.length === 0 ? <Alert tone="info">{t("shifts.closing.noTerminals")}</Alert> : null}
        <ul className="grid gap-3">
          {values.cardRows.map((row, index) => (
            <li key={row.rowId} className="grid gap-3 rounded-md border p-3 sm:grid-cols-2">
              <FormField label={t("shifts.closing.posTerminal")} required>
                {(control) => (
                  <Select
                    {...control}
                    className="min-h-11"
                    disabled={!editable}
                    value={row.posTerminalId}
                    onChange={(event) => patchRow(row.rowId, { posTerminalId: event.target.value })}
                  >
                    <option value="">{t("common.select")}</option>
                    {options.map((terminal) => (
                      <option key={terminal.id} value={terminal.id}>
                        {terminal.name} — {terminal.bankName}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label={t("shifts.records.amount")} required>
                {(control) => (
                  <MoneyInput
                    {...control}
                    className="min-h-11 text-base"
                    disabled={!editable}
                    value={row.amount}
                    onValueChange={(amount) => patchRow(row.rowId, { amount })}
                  />
                )}
              </FormField>
              <FormField label={t("shifts.closing.slipCount")}>
                {(control) => (
                  <Input
                    {...control}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    className="min-h-11"
                    disabled={!editable}
                    value={row.slipCount}
                    onChange={(event) => patchRow(row.rowId, { slipCount: event.target.value })}
                  />
                )}
              </FormField>
              <FormField label={t("shifts.closing.batchNo")} description={t("shifts.closing.batchNoHint")}>
                {(control) => (
                  <Input
                    {...control}
                    className="min-h-11"
                    disabled={!editable}
                    value={row.reference}
                    maxLength={60}
                    onChange={(event) => patchRow(row.rowId, { reference: event.target.value })}
                  />
                )}
              </FormField>
              {editable ? (
                <div className="sm:col-span-2">
                  <Button
                    variant="ghost"
                    size="lg"
                    aria-label={t("shifts.closing.removeTender", { no: index + 1 })}
                    onClick={() =>
                      onChange({ cardRows: values.cardRows.filter((item) => item.rowId !== row.rowId) })
                    }
                  >
                    <Trash2 aria-hidden="true" />
                    {t("common.delete")}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {editable ? (
          <div>
            <Button
              variant="outline"
              size="lg"
              onClick={() =>
                onChange({ cardRows: [...values.cardRows, emptyTenderRow(`card-${Date.now()}`, "CARD")] })
              }
            >
              <Plus aria-hidden="true" />
              {t("shifts.closing.addRow")}
            </Button>
          </div>
        ) : null}
        <p className="text-sm">
          {t("common.total")}:{" "}
          <MoneyText value={tenderRowsTotal(values.cardRows).toString()} className="font-semibold" />
        </p>
      </Section>
    </div>
  );
}

// ── 4. Veresiye / filo ve diğer araçlar ──────────────────────────────────────
function OtherStep({
  shiftId,
  closing,
  values,
  editable,
  onChange,
  problems,
}: {
  shiftId: string;
  closing: ShiftClosing;
  values: ClosingFormValues;
  editable: boolean;
  onChange: (changes: Partial<ClosingFormValues>) => void;
  problems: readonly string[];
}): ReactElement {
  const { t } = useTranslation();
  const derived = closing.tenders.filter((tender) => tender.derived);
  const patchRow = (rowId: string, changes: Partial<TenderRow>) =>
    onChange({
      otherRows: values.otherRows.map((row) => (row.rowId === rowId ? { ...row, ...changes } : row)),
    });

  return (
    <div className="grid gap-4">
      <ProblemList problems={problems} />

      <Section title={t("shifts.closing.derivedTitle")}>
        {/* Veresiye / filo İSTEMCİDEN ALINMAZ: vardiya içi fişlerden cari bazında türetilir. */}
        <p className="text-sm text-muted-foreground">{t("shifts.closing.derivedHint")}</p>
        {derived.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("shifts.closing.derivedEmpty")}</p>
        ) : (
          <SimpleTable
            caption={t("shifts.closing.derivedTitle")}
            head={
              <tr>
                <th scope="col" className={thClass}>
                  {t("shifts.records.creditSaleKind")}
                </th>
                <th scope="col" className={thClass}>
                  {t("shifts.records.counterparty")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("shifts.closing.slipCount")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("shifts.records.amount")}
                </th>
              </tr>
            }
          >
            {derived.map((tender) => (
              <tr key={tender.id} className="border-t">
                <td className={tdClass}>{SHIFT_TENDER_TYPE_LABELS[tender.tenderType]}</td>
                <td className={tdClass}>{tender.counterpartyName ?? "—"}</td>
                <td className={tdRightClass}>{tender.slipCount ?? "—"}</td>
                <td className={tdRightClass}>
                  <MoneyText value={tender.amount} />
                </td>
              </tr>
            ))}
          </SimpleTable>
        )}
        <div>
          <Button variant="outline" size="lg" asChild>
            <Link to="/vardiya/vardiyalar/$id" params={{ id: shiftId }} search={{ tab: "creditSales" }}>
              {t("shifts.closing.addCreditSale")}
            </Link>
          </Button>
        </div>
      </Section>

      <Section title={t("shifts.closing.otherTendersTitle")}>
        <p className="text-sm text-muted-foreground">{t("shifts.closing.otherTendersHint")}</p>
        <ul className="grid gap-3">
          {values.otherRows.map((row, index) => (
            <li key={row.rowId} className="grid gap-3 rounded-md border p-3 sm:grid-cols-3">
              <FormField label={t("shifts.closing.tenderType")}>
                {(control) => (
                  <Select
                    {...control}
                    className="min-h-11"
                    disabled={!editable}
                    value={row.tenderType}
                    onChange={(event) =>
                      patchRow(row.rowId, {
                        tenderType: event.target.value as (typeof OTHER_TENDER_TYPES)[number],
                      })
                    }
                  >
                    {OTHER_TENDER_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {SHIFT_TENDER_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label={t("shifts.records.amount")} required>
                {(control) => (
                  <MoneyInput
                    {...control}
                    className="min-h-11 text-base"
                    disabled={!editable}
                    value={row.amount}
                    onValueChange={(amount) => patchRow(row.rowId, { amount })}
                  />
                )}
              </FormField>
              <FormField label={t("shifts.closing.reference")}>
                {(control) => (
                  <Input
                    {...control}
                    className="min-h-11"
                    disabled={!editable}
                    value={row.reference}
                    maxLength={60}
                    onChange={(event) => patchRow(row.rowId, { reference: event.target.value })}
                  />
                )}
              </FormField>
              {editable ? (
                <div className="sm:col-span-3">
                  <Button
                    variant="ghost"
                    size="lg"
                    aria-label={t("shifts.closing.removeTender", { no: index + 1 })}
                    onClick={() =>
                      onChange({ otherRows: values.otherRows.filter((item) => item.rowId !== row.rowId) })
                    }
                  >
                    <Trash2 aria-hidden="true" />
                    {t("common.delete")}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {editable ? (
          <div>
            <Button
              variant="outline"
              size="lg"
              onClick={() =>
                onChange({
                  otherRows: [
                    ...values.otherRows,
                    emptyTenderRow(`other-${Date.now()}`, "VEHICLE_RECOGNITION"),
                  ],
                })
              }
            >
              <Plus aria-hidden="true" />
              {t("shifts.closing.addRow")}
            </Button>
          </div>
        ) : null}
        <p className="text-sm">
          {t("common.total")}:{" "}
          <MoneyText value={tenderRowsTotal(values.otherRows).toString()} className="font-semibold" />
        </p>
      </Section>
    </div>
  );
}

// ── 5. Özet ve teslim ────────────────────────────────────────────────────────
function SummaryStep({
  closing,
  values,
  editable,
  canSubmit,
  shiftStatus,
  preview,
  onChange,
}: {
  closing: ShiftClosing;
  values: ClosingFormValues;
  editable: boolean;
  canSubmit: boolean;
  shiftStatus: Shift["status"];
  preview: ReturnType<typeof declarationPreview>;
  onChange: (changes: Partial<ClosingFormValues>) => void;
}): ReactElement {
  const { t } = useTranslation();
  const direction = differenceDirection(preview.difference.toString());
  const settlement = closing.settlementPreview;

  return (
    <div className="grid gap-4">
      <Section title={t("shifts.closing.steps.summary")}>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryField label={t("shifts.closing.expected")}>
            <MoneyText value={closing.expectedAmount} />
          </SummaryField>
          <SummaryField label={t("shifts.closing.cashSales")}>
            <MoneyText value={preview.cashSales.toString()} />
          </SummaryField>
          <SummaryField label={t("shifts.closing.nonCash")}>
            <MoneyText value={preview.nonCash.toString()} />
          </SummaryField>
          <SummaryField label={t("shifts.closing.declared")}>
            <MoneyText value={preview.declared.toString()} />
          </SummaryField>
        </dl>

        <div className="grid gap-2 rounded-md border p-4">
          <p className="text-sm text-muted-foreground">{t("shifts.closing.difference")}</p>
          {/* Fark BÜYÜK puntoyla; yön ayrıca metinle yazılır (renk tek başına anlam taşımaz). */}
          <p className="flex flex-wrap items-center gap-3">
            <SignedMoneyText value={preview.difference.toString()} className="text-3xl font-bold" />
            <StatusBadge tone={differenceTone(preview.difference.toString(), closing.withinTolerance)}>
              {t(`shifts.closing.direction.${direction}`)}
            </StatusBadge>
          </p>
          <p className="text-sm text-muted-foreground">
            {t("shifts.closing.toleranceLimit", { limit: formatMinor(closing.toleranceLimit) })}
          </p>
          <p className="text-sm">
            {closing.withinTolerance
              ? t("shifts.closing.withinTolerance")
              : t("shifts.closing.outsideTolerance")}
          </p>
        </div>

        <div className="grid gap-1 rounded-md bg-muted/60 p-3 text-sm">
          <p>
            {t("shifts.closing.settlementPreview")}:{" "}
            <strong>
              {settlement.target === null
                ? t("shifts.closing.settlementNone")
                : SHIFT_SETTLEMENT_TARGET_LABELS[settlement.target]}
            </strong>{" "}
            <MoneyText value={settlement.amount} />
          </p>
          {settlement.fellBackToExpense ? (
            <p className="text-warning">{t("shifts.closing.settlementFallback")}</p>
          ) : null}
        </div>

        <FormField
          label={t("shifts.closing.explanation")}
          description={
            direction === "NONE"
              ? t("shifts.closing.explanationHint")
              : t("shifts.closing.explanationRequiredHint")
          }
        >
          {(control) => (
            <Textarea
              {...control}
              rows={3}
              maxLength={1000}
              disabled={!editable}
              value={values.explanation}
              onChange={(event) => onChange({ explanation: event.target.value })}
            />
          )}
        </FormField>

        {!canSubmit && editable ? (
          <Alert tone="info" title={t("shifts.closing.cannotSubmitTitle")}>
            {shiftStatus === "OPEN"
              ? t("shifts.closing.cannotSubmitNotClosing")
              : t("shifts.closing.cannotSubmitOther")}
          </Alert>
        ) : null}
      </Section>
    </div>
  );
}

// ── Sonuç ekranı ─────────────────────────────────────────────────────────────
function ResultPanel({
  closing,
  shiftId,
  nextMemberId,
  canReopen,
  downloading,
  onDownload,
  onReopen,
}: {
  closing: ShiftClosing;
  shiftId: string;
  nextMemberId: string | null;
  canReopen: boolean;
  downloading: boolean;
  onDownload: () => void;
  onReopen: () => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Card className="grid gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={SHIFT_CLOSING_STATUS_TONE[closing.status]}>
          {SHIFT_CLOSING_STATUS_LABELS[closing.status]}
        </StatusBadge>
        <SignedMoneyText value={closing.difference} className="text-lg font-semibold" />
        <StatusBadge tone={differenceTone(closing.difference, closing.withinTolerance)}>
          {t(`shifts.closing.direction.${differenceDirection(closing.difference)}`)}
        </StatusBadge>
      </div>
      <p className="text-sm text-muted-foreground">
        {closing.status === "ACCEPTED"
          ? t("shifts.closing.resultAccepted")
          : t("shifts.closing.resultPending")}
      </p>
      <div className="flex flex-wrap gap-2">
        {closing.status === "ACCEPTED" ? (
          <Button variant="outline" size="lg" loading={downloading} onClick={onDownload}>
            <Download aria-hidden="true" />
            {t("shifts.closing.handover")}
          </Button>
        ) : null}
        {nextMemberId ? (
          <Button size="lg" asChild>
            <Link
              to="/vardiya/vardiyalar/$id/hesap/$memberId"
              params={{ id: shiftId, memberId: nextMemberId }}
            >
              <ArrowRight aria-hidden="true" />
              {t("shifts.closing.nextMember")}
            </Link>
          </Button>
        ) : null}
        <Button variant="outline" size="lg" asChild>
          <Link to="/vardiya/vardiyalar/$id" params={{ id: shiftId }} search={{ tab: "closings" }}>
            {t("shifts.closing.backToShift")}
          </Link>
        </Button>
        {canReopen ? (
          <Button variant="outline" size="lg" onClick={onReopen}>
            <RotateCcw aria-hidden="true" />
            {t("shifts.closing.reopenClosing")}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
